import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';

export const isDuplicateNameError = (error: FetchBaseQueryError): boolean => {
    return error.status === 409;
};

export type ApiTokenSummary = {
    id: string;
    name: string;
    token_prefix: string;
    allowed_scopes: string[];
    created_at: string;
    created_by: string;
    last_used_at: string | null;
    revoked_at: string | null;
    revoked_by: string | null;
};

export type CreateApiTokenRequest = {
    name: string;
    allowed_scopes: string[];
};

export type CreateApiTokenResponse = {
    id: string;
    name: string;
    token: string;
    token_prefix: string;
    allowed_scopes: string[];
    created_at: string;
    created_by: string;
};

export type ListApiTokensResponse = {
    tokens: ApiTokenSummary[];
};

export type ListEngineApiTokensRequest = {
    includeRevoked?: boolean;
};

export type ScopeDescriptor = {
    name: string;
    description: string;
};

export type ListScopesResponse = {
    scopes: ScopeDescriptor[];
};

const ENGINE_API_TOKENS_LIST_TAG = { type: 'engineApiTokens', id: 'LIST' } as const;

const engineApiTokensEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        listEngineApiTokens: builder.query<ListApiTokensResponse, ListEngineApiTokensRequest | void>({
            query: arg => ({
                url: '/admin/tokens',
                method: 'GET',
                params: { include_revoked: arg?.includeRevoked ?? false },
            }),
            providesTags: [ENGINE_API_TOKENS_LIST_TAG],
        }),
        createEngineApiToken: builder.mutation<CreateApiTokenResponse, CreateApiTokenRequest>({
            query: body => ({
                url: '/admin/tokens',
                method: 'POST',
                body,
            }),
            invalidatesTags: [ENGINE_API_TOKENS_LIST_TAG],
        }),
        revokeEngineApiToken: builder.mutation<void, string>({
            query: id => ({
                url: `/admin/tokens/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [ENGINE_API_TOKENS_LIST_TAG],
        }),
        listEngineApiTokenScopes: builder.query<ListScopesResponse, void>({
            query: () => ({
                url: '/admin/tokens/scopes',
                method: 'GET',
            }),
        }),
    }),
});

export const {
    useListEngineApiTokensQuery,
    useCreateEngineApiTokenMutation,
    useRevokeEngineApiTokenMutation,
    useListEngineApiTokenScopesQuery,
} = engineApiTokensEngineSlice;

export default engineApiTokensEngineSlice;
